import {
  ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, finalize, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  EntityWithSocialProfiles, FieldStatus, FieldStatusInput, GetEntityById2GQL, SocialProfileDetails,
  UpdateSocialProfileGQL, UpdateSocialProfileInput
} from 'generated/graphql';

import { ParseSocialService } from 'app/social/parse-social.service';
import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import {
  AddOrUpdateSocialDialog
} from 'entity/dialogs/add-or-update-social/add-or-update-social.dialog';
import { getSafe, sortByLabel } from 'helpers';

@Component({
  selector: 'app-entity-social-table',
  templateUrl: './entity-social-table.component.html',
})
export class EntitySocialTableComponent
  extends WithDisposableSubject(WithChangesSubject(class { }))
  implements OnChanges, OnInit {
  isSaving = false;

  @Input() entityId: string;
  @Input() campaignId: string;
  @Input() showTaskDoneMark: boolean;

  entity: EntityWithSocialProfiles.Fragment = null;

  showBad = false;

  socialHandles: SocialProfileDetails.Fragment[] = null;

  sortByLabel = sortByLabel;


  constructor(
    private _getEntityGQL: GetEntityById2GQL,
    private _updateSocialProfile: UpdateSocialProfileGQL,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _parseSocialSvc: ParseSocialService,
    private _toastrService: ToastrService
  ) {
    super();
  }

  ngOnInit() {
    this.watchForChangeKeys('entityId', 'campaignId')
      .pipe(
        startWith<any, any>(null),
        takeUntil(this.disposer),
        map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
        filter(({ campaignId, entityId }) => !!campaignId || !!entityId),
        switchMap(({ campaignId, entityId }) =>
          this._getEntityGQL
            .watch(
              {
                entityId,
                campaignId,
                withToplines: true,
                withSocials: true
              },
              {
                fetchPolicy: 'cache-first',
              }
            )
            .valueChanges.pipe(takeUntil(this.disposer))
        ),
        map(({ data }) => data?.getEntity)
      )
      .subscribe((entity) => {
        this.entity = entity as any;
        this.socialHandles = entity.socialProfiles as any;
        this._cd.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  onAddNew() {
    AddOrUpdateSocialDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        isCreate: true,
      },
    });
  }

  onEdit(socialProfile: SocialProfileDetails.Fragment) {
    AddOrUpdateSocialDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        isCreate: false,
        socialProfile
      },
    });
  }

  onSetStatus(
    socialProfile: SocialProfileDetails.Fragment,
    status: 'bad' | 'verified' | 'user_added'
  ) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    if (status === 'bad') {
      const shouldMarkBad = confirm(
        'Are you sure you would like to mark this social media profile as bad?'
      );
      if (!shouldMarkBad) {
        return;
      }
    }
    const input: UpdateSocialProfileInput = {
      campaignId: this.entity.campaignContextId,
      entityId: this.entity.id,
      socialProfileId: socialProfile.id,
      status: status as FieldStatusInput,
    };

    this._updateSocialProfile
      .mutate({ input })
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(
        (_) => this._toastrService.success('Changes saved'),
        err => this._toastrService.error(err)
      );
  }

  isVerified(socialProfile: SocialProfileDetails.Fragment) {
    return socialProfile.status === FieldStatus.Verified;
  }

  isBad(socialProfile: SocialProfileDetails.Fragment) {
    return getSafe(() => socialProfile.status === 'bad', true);
  }

  getBadCount(socialProfiles: SocialProfileDetails.Fragment[]) {
    return getSafe(() => socialProfiles.filter((i) => this.isBad(i)).length, 0);
  }

  getSocialURL(socialProfile: SocialProfileDetails.Fragment): string {
    const parsedResult = this._parseSocialSvc.parseSocial(socialProfile.socialNetwork.id, socialProfile.profile);
    try {
      if (new URL(parsedResult.sanitized)) { return parsedResult.sanitized; }
    } catch (err) {
      return null;
    }
  }

  getSocialLabel(socialProfile: SocialProfileDetails.Fragment): string {
    if (socialProfile.socialNetwork.id === 'other') {
      return socialProfile.label;
    }
    if (socialProfile.socialNetwork.id === 'website') {
      return socialProfile.socialNetwork.label;
    }

    return socialProfile.profile;
  }
}
